import React from 'react';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { useDispatch, useSelector } from 'react-redux';

import withStyles from '~src/lib/isomorphic-style-loader/withStyles';
import commandUrl from '~src/lib/commandUrl';
import isHubAccessBlocked from '~src/lib/isHubAccessBlocked';
import { logout, fetchSwitchTenant } from '~src/actions/userActions';

import s from './MigrationTakeover.scss';

/*
 * Full-page block for a retailer's own users once hub-backend reports Hub
 * access as blocked (CPMT-5928) — decided per retailer, independent of going
 * live in Command, so a live retailer can still reach Hub for a grace period.
 * Narvar staff keep a functional Hub behind the migration banner. Replaces
 * every logged-in route, so users who also belong to a retailer that can
 * still use Hub get a switcher — nothing else in the app can reach one while
 * the takeover is up.
 */
export const MigrationTakeover = () => {
  const dispatch = useDispatch();

  const { retailerId, retailerIdToRetailerInfo } = useSelector(
    ({ userReducer: { user } = {} }) => ({
      retailerId: user ? user.retailerId : undefined,
      retailerIdToRetailerInfo: user ? user.retailerIdToRetailerInfo || {} : {},
    }),
  );

  // The signedin payload is the only source of retailerId; normalize it so the
  // strict comparisons against the numeric option values hold even if the
  // backend serializes the id as a string.
  const currentRetailerId = Number(retailerId);

  // Blocked retailers would only lead back to this screen, so the switcher
  // keeps the current retailer — it is the selected value — plus the retailers
  // Hub still works for. Absent migration data means not blocked, matching the
  // gate that put the user here.
  const switcherOptions = Object.entries(retailerIdToRetailerInfo)
    .filter(
      ([id, info]) =>
        Number(id) === currentRetailerId || !isHubAccessBlocked(info.migration),
    )
    .map(([id, info]) => ({ id: Number(id), name: info.retailer_name }));

  const handleSwitch = event => {
    const nextRetailerId = Number(event.target.value);

    if (nextRetailerId === currentRetailerId) return;

    dispatch(fetchSwitchTenant(nextRetailerId));
  };

  return (
    <main className={s.takeover}>
      <div className={s.content}>
        <h1 className={s.heading}>
          Your account has moved to Narvar Command, the redesigned platform for
          managing your Narvar experience.
        </h1>
        <Button
          color="secondary"
          variant="contained"
          href={commandUrl()}
          style={{
            whiteSpace: 'nowrap',
            height: '40px',
            borderRadius: '4px',
            color: '#fff',
          }}
        >
          Go to Command
        </Button>
        {switcherOptions.length > 1 && (
          <TextField
            select
            size="small"
            variant="outlined"
            label="Switch account"
            value={currentRetailerId}
            onChange={handleSwitch}
            className={s.switcher}
          >
            {switcherOptions.map(retailer => (
              <MenuItem key={retailer.id} value={retailer.id}>
                {retailer.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <button
          type="button"
          className={s.logout}
          onClick={() => dispatch(logout())}
        >
          Log out
        </button>
      </div>
    </main>
  );
};

export default withStyles(s)(MigrationTakeover);
